import React, { useCallback } from 'react';
import {
  Edit,
  TextField,
  DateField,
  ReferenceManyField,
  Datagrid,
  useRedirect,
  useNotify,
  SimpleForm,
  Pagination,
} from 'react-admin';

import {
  Button,
  Card,
  Dialog,
  DialogContent,
} from '@mui/material';

import HistoryIcon from '@mui/icons-material/History';

import {
  isArray, isEmpty, isObject, omit,
} from 'lodash';

import ReferenceMemberField from '../../customFields/ReferenceMemberField';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';
import ruleTypeMapper from './ruleTypeMapper';
import useDialogStatus from '../../hooks/useDialogStatus';
import RuleBreadcrumb from './RuleBreadcrumb';
import useRule from '../../hooks/useRule';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import EditToolbar from '../layout/EditToolbar';
import useRuleVersion from '../../hooks/useRuleVersion';
import errorNotification from '../../utilities/errorNotification';
import { PendingReviewProvider } from '../../context/PendingReviewContext';
import usePendingReview from '../../hooks/usePendingReview';
import actions from '../../constants/actions';

const RuleEdit = () => {
  const redirect = useRedirect();
  const notify = useNotify();

  const {
    ruleType,
    view,
  } = useRule();
  const { version } = useRuleVersion();

  const { open, openDialog, closeDialog } = useDialogStatus();

  const { pendingReview } = usePendingReview(actions.RULE_VERSION_PUBLISH, { ruleType });

  const onSuccess = () => {
    notify('Rule updated');
    redirect('list', 'rules', undefined, {}, { record: { ruleType, view, version } });
  };

  const onError = useCallback((e: unknown) => {
    notify(errorNotification(e), { type: 'error' });
  }, [notify]);

  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <PendingReviewProvider value={!!pendingReview}>
      <Edit
        mutationOptions={{ onSuccess, onError }}
        mutationMode="pessimistic"
        // The score of a Rule is frozen during the Risk Factor migration, so it's never sent
        transform={(data) => Object.fromEntries(Object.entries(omit(data, 'score')).map(([k, v]) => {
          if (!isObject(v) || isArray(v)) {
            return [k, v];
          }
          return [k, isEmpty(v) ? undefined : v];
        }))}
        actions={(
          <EditToolbar backButtonLabel="Exit" backState={{ record: { ruleType, view, version } }}>
            <Button onClick={openDialog} variant="outlined" startIcon={<HistoryIcon />}>Audit trail</Button>
          </EditToolbar>
        )}
      >
        <>
          <RuleBreadcrumb />
          <SimpleForm mode="all" toolbar={false}>
            {React.createElement(ruleTypeMapper[ruleType])}
            <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="xl">
              <DialogContent>
                <Card variant="outlined">
                  <ReferenceManyField
                    reference="audit-tracks"
                    target="resource.id"
                    filter={{ 'resource.type': resourceTypes.RULE }}
                    sort={{ field: 'date', order: 'DESC' }}
                    pagination={<Pagination />}
                  >
                    <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
                      <TextField sortable={false} source="id" label="ID" />
                      <ReferenceMemberField label="User" />
                      <TextField sortable={false} source="description" />
                      <DateField sortable source="date" showTime />
                    </Datagrid>
                  </ReferenceManyField>
                </Card>
              </DialogContent>
            </Dialog>
          </SimpleForm>
        </>
      </Edit>
    </PendingReviewProvider>
  );
};

export default RuleEdit;
